import { signal, computed, type Signal } from '@preact/signals-react'
import type { TestState, TestStateSection } from '@merels/web/deps/debug/positions/positions'

const RECENTLY_USED_KEY = 'merels_recently_used_states'
const MAX_RECENT_STATES = 10

function getRecentlyUsedStates(gameId: string): string[] {
  try {
    const stored = sessionStorage.getItem(RECENTLY_USED_KEY)
    if (!stored) return []
    const all = JSON.parse(stored) as Record<string, string[]>
    return all[gameId] || []
  } catch {
    return []
  }
}

function addRecentlyUsedState(gameId: string, stateName: string): void {
  try {
    const stored = sessionStorage.getItem(RECENTLY_USED_KEY)
    const all: Record<string, string[]> = stored ? JSON.parse(stored) : {}
    const recent = all[gameId] || []
    const filtered = recent.filter(s => s !== stateName)
    const updated = [stateName, ...filtered].slice(0, MAX_RECENT_STATES)
    all[gameId] = updated
    sessionStorage.setItem(RECENTLY_USED_KEY, JSON.stringify(all))
  } catch {
    // Ignore sessionStorage errors
  }
}

export interface TestStateSelectorState {
  searchText: Signal<string>
  selectedState: Signal<TestState | null>
  editableStateJson: Signal<string>
  isEditing: Signal<boolean>
  error: Signal<string | null>
  isApplying: Signal<boolean>
  loadedStateName: Signal<string | null>
  showJsonViewer: Signal<boolean>
  expandedSections: Signal<Set<string>>
  sections: Signal<TestStateSection[]>
  filteredSections: Signal<TestStateSection[]>
  recentlyUsed: Signal<string[]>
  
  // Actions
  setSearchText: (text: string) => void
  setSelectedState: (state: TestState | null) => void
  setEditableStateJson: (json: string) => void
  setIsEditing: (editing: boolean) => void
  setError: (error: string | null) => void
  setIsApplying: (applying: boolean) => void
  setLoadedStateName: (name: string | null) => void
  setShowJsonViewer: (show: boolean) => void
  toggleSection: (title: string) => void
  expandAllSections: () => void
  markStateAsUsed: (stateName: string) => void
  loadSections: (gameId: string) => void
}

export function makeTestStateSelectorState(gameId: string, gameRegistry: any): TestStateSelectorState {
  const searchText = signal<string>('')
  const selectedState = signal<TestState | null>(null)
  const editableStateJson = signal<string>('')
  const isEditing = signal<boolean>(false)
  const error = signal<string | null>(null)
  const isApplying = signal<boolean>(false)
  const loadedStateName = signal<string | null>(null)
  const showJsonViewer = signal<boolean>(false)
  const expandedSections = signal<Set<string>>(new Set())
  const sections = signal<TestStateSection[]>([])
  const recentlyUsed = signal<string[]>(getRecentlyUsedStates(gameId))

  // Computed: filtered sections based on search text
  const filteredSections = computed(() => {
    const sectionsValue = sections.value
    const searchValue = searchText.value.trim().toLowerCase()
    
    if (sectionsValue.length === 0) return []
    if (searchValue === '') return sectionsValue

    return sectionsValue.map(section => {
      const posList = (section.positions || section.states || []).filter((pos: TestState) =>
        pos.name.toLowerCase().includes(searchValue) ||
        pos.displayName.toLowerCase().includes(searchValue) ||
        pos.description?.toLowerCase().includes(searchValue)
      )
      return {
        ...section,
        positions: posList,
        states: posList,
      }
    }).filter(section => (section.positions || section.states || []).length > 0)
  })

  return {
    searchText,
    selectedState,
    editableStateJson,
    isEditing,
    error,
    isApplying,
    loadedStateName,
    showJsonViewer,
    expandedSections,
    sections,
    filteredSections,
    recentlyUsed,

    setSearchText: (text: string) => {
      searchText.value = text
      // When filtering, expand all sections
      if (text.trim() !== '') {
        const allTitles = new Set(sections.value.map(s => s.title))
        expandedSections.value = allTitles
      }
    },

    setSelectedState: (state: TestState | null) => {
      selectedState.value = state
      if (state) {
        editableStateJson.value = JSON.stringify(state.state, null, 2)
      }
    },

    setEditableStateJson: (json: string) => {
      editableStateJson.value = json
    },

    setIsEditing: (editing: boolean) => {
      isEditing.value = editing
    },

    setError: (err: string | null) => {
      error.value = err
    },

    setIsApplying: (applying: boolean) => {
      isApplying.value = applying
    },

    setLoadedStateName: (name: string | null) => {
      loadedStateName.value = name
    },

    setShowJsonViewer: (show: boolean) => {
      showJsonViewer.value = show
      if (!show) {
        isEditing.value = false
      }
    },

    toggleSection: (title: string) => {
      const next = new Set(expandedSections.value)
      if (next.has(title)) {
        next.delete(title)
      } else {
        next.add(title)
      }
      expandedSections.value = next
    },

    expandAllSections: () => {
      const allTitles = new Set(sections.value.map(s => s.title))
      expandedSections.value = allTitles
    },

    markStateAsUsed: (stateName: string) => {
      addRecentlyUsedState(gameId, stateName)
      recentlyUsed.value = getRecentlyUsedStates(gameId)
    },

    loadSections: (newGameId: string) => {
      const resolved = gameRegistry?.getResolvedModule?.(newGameId)
      const rawSections = resolved?.positions || resolved?.testStates || []
      const loaded = rawSections.map((sec: any) => ({
        title: sec.title,
        states: sec.positions || sec.states || [],
        positions: sec.positions || sec.states || []
      }))
      sections.value = loaded
      recentlyUsed.value = getRecentlyUsedStates(newGameId)
    },
  }
}

